import React, { useState, useRef } from "react";

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [company, setCompany] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setSuccess(false);

    // Honeypot check - bots fill this hidden field.
    if (company) return;

    if (!name.trim() || !email.trim() || !message.trim()) {
      setError("Please fill in all fields.");
      return;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          message: message.trim(),
          company: company.trim(),
        }),
        headers: { "Content-Type": "application/json" },
      });

      if (response.ok) {
        setName("");
        setEmail("");
        setMessage("");
        setCompany("");
        setSuccess(true);
        if (formRef.current) {
          formRef.current.reset();
        }
      } else {
        const payload = await response.json().catch(() => null);
        setError(payload?.error || "Something went wrong. Please try again.");
      }
    } catch {
      setError("Could not send message. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section">
      <div className="section-inner">
        <div className="contact__content">
          <div className="contact__header">
            <p className="contact__label">Contact</p>
            <h2 className="contact__title">Get in Touch</h2>
            <p className="contact__subtitle">
              Founder, product, and technical inquiries are welcome. Send me a
              message.
            </p>
          </div>

          <form ref={formRef} className="contact-form" onSubmit={handleSubmit}>
            {/* Honeypot field - hidden from humans, visible to bots. */}
            <div className="form-field--hp" aria-hidden="true">
              <label htmlFor="company">Company</label>
              <input
                type="text"
                id="company"
                name="company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                autoComplete="off"
                tabIndex={-1}
              />
            </div>

            <div className="form-field">
              <label className="form-field__label" htmlFor="name">
                Name
              </label>
              <input
                className="form-field__input"
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
              />
            </div>

            <div className="form-field">
              <label className="form-field__label" htmlFor="email">
                Email
              </label>
              <input
                className="form-field__input"
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
              />
            </div>

            <div className="form-field">
              <label className="form-field__label" htmlFor="message">
                Message
              </label>
              <textarea
                className="form-field__textarea"
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What's on your mind?"
              />
            </div>

            {error && <p className="contact-form__error">{error}</p>}
            {success && (
              <p className="contact-form__success">
                Thanks for reaching out. I'll get back to you soon.
              </p>
            )}

            <button
              type="submit"
              className="contact-form__submit"
              disabled={submitting}
            >
              {submitting ? "Sending..." : "Send Message"}
            </button>
          </form>

          <div className="contact__links">
            <a
              className="contact__link"
              href="https://github.com/KoreyCollazo"
              target="_blank"
              rel="noopener noreferrer"
            >
              GitHub
            </a>
            <a
              className="contact__link"
              href="https://www.linkedin.com/in/korey-collazo/"
              target="_blank"
              rel="noopener noreferrer"
            >
              LinkedIn
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
